import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { SOURCE_OPTIONS } from "@/lib/sources";

// --- Máscaras e validações ---
export const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export function onlyDigits(s) { return (s || "").replace(/\D/g, ""); }
export function maskCpf(s) { const d = onlyDigits(s).slice(0, 11); return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1-$2"); }
export function maskCnpj(s) { const d = onlyDigits(s).slice(0, 14); return d.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{4})/, "$1/$2").replace(/(\d{4})(\d)/, "$1-$2"); }
export function maskCep(s) { const d = onlyDigits(s).slice(0, 8); return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d; }
export function maskPhone(s) { const d = onlyDigits(s).slice(0, 11); if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2"); return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2"); }

export function validCpf(raw) {
  const s = onlyDigits(raw); if (s.length !== 11 || /^(\d)\1+$/.test(s)) return false;
  let sum = 0; for (let i = 0; i < 9; i++) sum += parseInt(s[i]) * (10 - i);
  let d1 = 11 - (sum % 11); if (d1 > 9) d1 = 0; if (d1 !== parseInt(s[9])) return false;
  sum = 0; for (let i = 0; i < 10; i++) sum += parseInt(s[i]) * (11 - i);
  let d2 = 11 - (sum % 11); if (d2 > 9) d2 = 0; return d2 === parseInt(s[10]);
}
export function validCnpj(raw) {
  const s = onlyDigits(raw); if (s.length !== 14 || /^(\d)\1+$/.test(s)) return false;
  const calc = (base) => {
    const w = base.length === 12 ? [5,4,3,2,9,8,7,6,5,4,3,2] : [6,5,4,3,2,9,8,7,6,5,4,3,2];
    let sum = 0; for (let i = 0; i < w.length; i++) sum += parseInt(base[i]) * w[i];
    const r = sum % 11; return r < 2 ? 0 : 11 - r;
  };
  return calc(s.slice(0, 12)) === parseInt(s[12]) && calc(s.slice(0, 13)) === parseInt(s[13]);
}

const EMPTY = {
  person_type: "pf", document: "", legal_name: "", trading_name: "",
  birth_date: "", cep: "", street: "", number: "", complement: "",
  neighborhood: "", city: "", uf: "", rg: "", landline: "", source: "",
};

export function CustomerProfileFields({ value, onChange, idPrefix = "customer", requireSource = false }) {
  const v = { ...EMPTY, ...value };
  const set = (patch) => onChange({ ...v, ...patch });

  const [cnpjLookup, setCnpjLookup] = useState(false);
  const [cepLookup, setCepLookup] = useState(false);
  const lastCnpj = useRef(""); const lastCep = useRef("");

  const docError = v.document && ((v.person_type === "pf" && !validCpf(v.document)) || (v.person_type === "pj" && !validCnpj(v.document)));

  // CNPJ lookup via BrasilAPI (public, sem auth) ao bater 14 dígitos válidos.
  useEffect(() => {
    const d = onlyDigits(v.document);
    if (v.person_type !== "pj" || d.length !== 14 || !validCnpj(d) || lastCnpj.current === d) return;
    lastCnpj.current = d;
    setCnpjLookup(true);
    axios.get(`https://brasilapi.com.br/api/cnpj/v1/${d}`, { timeout: 8000, withCredentials: false })
      .then(({ data }) => {
        onChange({
          ...v,
          legal_name: data.razao_social || v.legal_name,
          trading_name: data.nome_fantasia || v.trading_name,
          cep: onlyDigits(data.cep || "") || v.cep,
          street: data.logradouro || v.street,
          number: data.numero ? String(data.numero) : v.number,
          complement: data.complemento || v.complement,
          neighborhood: data.bairro || v.neighborhood,
          city: data.municipio || v.city,
          uf: (data.uf || "").toUpperCase() || v.uf,
        });
      })
      .catch(() => { /* silencioso: deixa preencher manual */ })
      .finally(() => setCnpjLookup(false));
  }, [v.document, v.person_type]);

  // CEP via ViaCEP
  useEffect(() => {
    const c = onlyDigits(v.cep);
    if (c.length !== 8 || lastCep.current === c) return;
    lastCep.current = c;
    setCepLookup(true);
    axios.get(`https://viacep.com.br/ws/${c}/json/`, { timeout: 6000, withCredentials: false })
      .then(({ data }) => {
        if (!data || data.erro) return;
        onChange({
          ...v,
          street: data.logradouro || v.street,
          neighborhood: data.bairro || v.neighborhood,
          city: data.localidade || v.city,
          uf: (data.uf || "").toUpperCase() || v.uf,
        });
      }).catch(() => {})
      .finally(() => setCepLookup(false));
  }, [v.cep]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Tipo</Label>
          <Select value={v.person_type} onValueChange={(x) => set({ person_type: x, document: "" })}>
            <SelectTrigger data-testid={`${idPrefix}-person-type`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="pf">Pessoa Física</SelectItem>
              <SelectItem value="pj">Pessoa Jurídica</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-2">
            {v.person_type === "pj" ? "CNPJ" : "CPF"}
            {cnpjLookup && <Loader2 className="h-3 w-3 animate-spin text-zinc-500" />}
          </Label>
          <Input data-testid={`${idPrefix}-document`} inputMode="numeric"
            value={v.person_type === "pj" ? maskCnpj(v.document) : maskCpf(v.document)}
            onChange={(e) => set({ document: onlyDigits(e.target.value) })}
            placeholder={v.person_type === "pj" ? "00.000.000/0000-00" : "000.000.000-00"}
            className={`mt-1 bg-zinc-900 border-zinc-800 ${docError ? "border-red-800" : ""}`} />
          {docError && <p className="text-[11px] text-red-400 mt-1">{v.person_type === "pj" ? "CNPJ" : "CPF"} inválido</p>}
        </div>
      </div>
      {v.person_type === "pj" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Razão social</Label>
            <Input data-testid={`${idPrefix}-legal-name`} value={v.legal_name} onChange={(e) => set({ legal_name: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Nome fantasia</Label>
            <Input data-testid={`${idPrefix}-trading-name`} value={v.trading_name} onChange={(e) => set({ trading_name: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
          </div>
        </div>
      )}
      {v.person_type === "pf" && (
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Data de nascimento</Label>
          <Input data-testid={`${idPrefix}-birth`} type="date" value={v.birth_date || ""} onChange={(e) => set({ birth_date: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800 w-full min-w-0" style={{ WebkitAppearance: "none", fontSize: 16 }} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-2">CEP {cepLookup && <Loader2 className="h-3 w-3 animate-spin text-zinc-500" />}</Label>
          <Input data-testid={`${idPrefix}-cep`} inputMode="numeric" value={maskCep(v.cep)} onChange={(e) => set({ cep: onlyDigits(e.target.value) })} placeholder="00000-000" className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">UF</Label>
          <Select value={v.uf} onValueChange={(x) => set({ uf: x })}>
            <SelectTrigger data-testid={`${idPrefix}-uf`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
              {UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Rua / Logradouro</Label>
          <Input data-testid={`${idPrefix}-street`} value={v.street} onChange={(e) => set({ street: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Número</Label>
          <Input data-testid={`${idPrefix}-number`} value={v.number} onChange={(e) => set({ number: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Complemento</Label>
          <Input data-testid={`${idPrefix}-complement`} value={v.complement} onChange={(e) => set({ complement: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Bairro</Label>
          <Input data-testid={`${idPrefix}-neighborhood`} value={v.neighborhood} onChange={(e) => set({ neighborhood: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-mono-caps text-zinc-400">Cidade</Label>
        <Input data-testid={`${idPrefix}-city`} value={v.city} onChange={(e) => set({ city: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">RG</Label>
          <Input data-testid={`${idPrefix}-rg`} value={v.rg || ""} onChange={(e) => set({ rg: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Telefone fixo</Label>
          <Input data-testid={`${idPrefix}-landline`} inputMode="tel" value={v.landline || ""} onChange={(e) => set({ landline: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-mono-caps text-zinc-400">Como chegou{requireSource ? " *" : ""}</Label>
        <Select value={v.source || ""} onValueChange={(x) => set({ source: x || null })}>
          <SelectTrigger data-testid={`${idPrefix}-source`} className={`mt-1 bg-zinc-900 ${requireSource && !v.source ? "border-amber-600" : "border-zinc-800"}`}><SelectValue placeholder="(selecionar)" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            {SOURCE_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${idPrefix}-source-${k}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function customerProfilePayload(profile) {
  if (!profile) return {};
  const p = { ...profile };
  if (p.document) p.document = onlyDigits(p.document);
  if (p.cep) p.cep = onlyDigits(p.cep);
  if (p.uf) p.uf = (p.uf || "").toUpperCase();
  Object.keys(p).forEach((k) => { if (p[k] === "") p[k] = null; });
  return p;
}
